import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastAutoHideDuration } from 'Src/consts/duration';
import { AlertProps } from 'Src/feature/alert/Alert';
import { Check } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { useCurrentCheck } from 'Src/feature/check/useCurrentCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { DiscountReason, PromoCode, StandardDiscountWithSchedules } from 'Src/feature/discount/discountService';
import { useDiscount } from 'Src/feature/discount/useDiscount';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useCurrentOperator } from 'Src/feature/operator/useCurrentOperator';
import { useToast } from 'Src/feature/toasts/useToast';
import { sortByName } from 'Src/utils/sort';
import { definitions } from 'Src/vendor/posapi/pos';

const sectionId = 'useCheckDiscount';
type AutoDiscountOptions = definitions['check.AutoDiscountOptions'];
type CheckDiscount = Check['info']['discounts'][number];
type CheckDiscountUI = StandardDiscountWithSchedules & {
  is: {
    /**
     * @description Indicates the discount was applied to the check/item.
     * It needs to be used in conjunction with enabled flag.
     */
    applied: boolean;
    /**
     * @description Indicates if the discount is enabled in its application to the check/item.
     * It could be false if either of these things have happened:
     * - Discount removed from item (sets status to Removed)
     * - Discount is auto-applied and the server will automatically handle changing its status
     *   to Enabled/Disabled as the check/item progresses.
     */
    enabled: boolean;
  }
  onCheck?: CheckDiscount;
}

const useCheckDiscount = () => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const toast = useToast();

  const discounts = useDiscount.query.getAll();
  const currentCheck = useCurrentCheck();
  const currentOperator = useCurrentOperator();

  const createDiscount = useCheck.mutate.createDiscount();
  const removeDiscount = useCheck.mutate.removeDiscount();
  const updateAutoDiscount = useCheck.mutate.updateAutoDiscount();

  const showToast = (options: { message: string; severity?: AlertProps['severity'] }) => {
    toast.make({
      id: options.message,
      type: 'alert',
      autoHideDuration: ToastAutoHideDuration.Default,
      props: {
        title: t('discount.labels.checkDiscountTitle'),
        message: options.message,
        severity: options.severity || 'warning',
      },
    }).show();
  };

  const unpaidAmount = currentCheck.query.data?.balance?.unpaid || 0;
  const checkAmount = currentCheck.query.data?.total || 0;

  const manageDiscountsEntitlement = currentOperator.permission('pos.checks.discounts.manage');
  // TODO - pos.checks.discounts.create and pos.checks.discounts.delete will be depracated in the future, per product
  const createDiscountsEntitlement = currentOperator.permission('pos.checks.discounts.create');

  const operatorCanManageManual = useCallback((discount: StandardDiscountWithSchedules) => {
    // need to account for old entitlement (pos.checks.discounts.create) until merchants are all converted
    if (createDiscountsEntitlement.isAllowed) return true;
    // permission must be granted
    if (!manageDiscountsEntitlement.isAllowed) return false;
    // discount must exist on metadata
    if (manageDiscountsEntitlement.metadata?.allowAll || (manageDiscountsEntitlement.metadata?.standardDiscountIds ?? []).includes(discount.id)) return true;
    // discount does not exist on metadata
    return false;
  }, [
    createDiscountsEntitlement.isAllowed,
    manageDiscountsEntitlement.isAllowed,
    manageDiscountsEntitlement.metadata?.allowAll,
    manageDiscountsEntitlement.metadata?.standardDiscountIds,
  ]);

  const operatorCanManageAuto = useCallback((discount: Check['info']['discounts'][number]) => {
    // need to account for old entitlement (pos.checks.discounts.create) until merchants are all converted
    if (createDiscountsEntitlement.isAllowed) return true;
    // permission must be granted
    if (!manageDiscountsEntitlement.isAllowed) return false;
    // discount must exist on metadata
    if (
      manageDiscountsEntitlement.metadata?.allowAll ||
      (!!discount.standardDiscountId && (manageDiscountsEntitlement.metadata?.standardDiscountIds ?? []).includes(discount.standardDiscountId))
    ) {
      return true;
    }
    // discount does not exist on metadata
    return false;
  }, [
    createDiscountsEntitlement.isAllowed,
    manageDiscountsEntitlement.isAllowed,
    manageDiscountsEntitlement.metadata?.allowAll,
    manageDiscountsEntitlement.metadata?.standardDiscountIds,
  ]);

  const isApplicable = (discount: StandardDiscountWithSchedules) => {
    if (!discount.isEnabled) return false;
    // If discount.application is of type Check then check level discount can be applied to only specific items on a check
    // If discount.application is of type Item ( CheckItem ) then it is meant to only apply to a check item
    // If discount.application is of type Both then it can be applied to either check or check item
    if (discount.application !== 'Check' && discount.application !== 'Both') return false;
    // should not hit this based on above criteria
    return true;
  };

  const findDiscountOnCheck = useCallback((opts: { standardDiscountId?: StandardDiscountWithSchedules['id'] }): CheckDiscount | undefined => {
    if (!currentCheck.query.data) return;
    // TODO: remove when backend resolve null arrays coming back instead of empty array
    const foundCheckDiscount = (currentCheck.query.data.info?.discounts || []).find((discount) => {
      // do not pull any they may have been removed previously
      if (discount.status !== 'Enabled') return;
      const { standardDiscountId } = discount;
      return standardDiscountId === opts.standardDiscountId;
    });
    if (!foundCheckDiscount) return undefined;
    return foundCheckDiscount;
  }, [currentCheck.query.data]);

  const discountUIs = useMemo(() => discounts.data
    // filtered subset of applicable and allowed discounts
    ?.filter((discount) => isApplicable(discount) && operatorCanManageManual(discount))
    // add some extra properties to the discount model for ui
    .map((discount) => {
      const is = { applied: false, enabled: false };
      const existingDiscount = findDiscountOnCheck({ standardDiscountId: discount.id });
      is.applied = !!existingDiscount;
      is.enabled = existingDiscount?.status === 'Enabled';
      return Object.assign(discount, { is, onCheck: existingDiscount });
    })
    // sort discounts alphabetically
    .sort(sortByName), [discounts.data, findDiscountOnCheck, operatorCanManageManual]);

  const autoDiscountUIs = useMemo(() => (currentCheck.query.data?.info.discounts ?? [])
    .filter((d) => d.doAutoApply && operatorCanManageAuto(d))
    .map((d) => {
      const is = { applied: !d.isAutoDisabled, enabled: d.status === 'Enabled' };
      return Object.assign(d, { is });
    })
    .sort(sortByName), [currentCheck.query.data?.info.discounts, operatorCanManageAuto]);

  const getPromoCodeFromDiscount = (code: string, discount: StandardDiscountWithSchedules) =>
    discount.promoCodes.find((promoCode) => promoCode.code.toUpperCase() === code.toUpperCase());

  const getDiscountFromPromoCode = (code: string) => discountUIs?.find((discount) => getPromoCodeFromDiscount(code, discount) !== undefined);

  const has = {
    any: (discountUIs && discountUIs?.length > 0) || autoDiscountUIs.length > 0,
    someWithPromoCodes: discountUIs && discountUIs?.length > 0 && discountUIs?.some(({ promoCodes }) => promoCodes && promoCodes.length)
  };

  // stored active values are necessary because
  // 1. ui
  // 2. save reference to active discount
  // 3. open modal that needs access to active value
  // 4. modal appends more values
  // 5. save discount updates after/from multiple ui layers

  const [activePromoCode, setActivePromoCode] = useState<PromoCode | undefined>(undefined);
  const [activeReason, setActiveReason] = useState<DiscountReason | undefined>(undefined);
  const [activeDiscount, setActiveDiscount] = useState<StandardDiscountWithSchedules | undefined>(undefined);
  const [activeOpenValue, setActiveOpenValue] = useState<number | undefined>(undefined);

  const reasonModal = useModalControl();
  const openDiscountModal = useModalControl();

  const onReasonSelect = (reasonId: number) => {
    const reason = activeDiscount?.reasons.find(({ id }) => id === reasonId);
    if (!reason) {
      return showToast({ message: t('discount.validation.reasonNotFound', { reasonId }) });
    }
    setActiveReason(reason);
    reasonModal.close();
    sendStoreValues({ reason });
  };

  const onOpenDiscountApply = (options: { reasonId: number, openValue: number, note?: string }) => {
    const { reasonId, openValue, note } = options;
    openDiscountModal.close();
    const reason = activeDiscount?.reasons.find(({ id }) => id === reasonId);
    if (!reason) {
      return showToast({ message: t('discount.validation.reasonNotFound', { reasonId }) });
    }
    setActiveReason(reason);
    setActiveOpenValue(openValue);
    sendStoreValues({ reason, openValue, note });
  };

  const sendStoreValues = (options?: { reason?: DiscountReason, openValue?: number, note?: string }) => {
    const { reason, openValue, note } = options ?? {};
    // this is mainly just for developer debugging and as a typescript guard
    // all other casting in the payload will get caught by the server
    if (!activeDiscount) return showToast({ message: t('discount.validation.notSelected') });
    if (!currentCheck.query.data) return showToast({ message: t('discount.validation.checkMissing') });

    log.debug('sendStoreValues()', { activeDiscount, reason, activePromoCode, openValue, note });

    // blank out any stored values ( even if the socket fails )
    setActivePromoCode(undefined);
    setActiveOpenValue(undefined);
    setActiveReason(undefined);
    setActiveDiscount(undefined);

    createDiscount.mutate({
      checkId: currentCheck.query.data?.id,
      discount: {
        application: 'Check',
        standardDiscountId: activeDiscount.id as number,
        type: activeDiscount.type === 'FixedPercent' || activeDiscount.type === 'OpenPercent'
          ? 'Percent'
          : 'Amount',
        value: (activeDiscount.type === 'FixedAmount' || activeDiscount.type === 'FixedPercent'
          ? activeDiscount.fixedValue
          : openValue) as number,
        reason: reason?.reason ?? '',
        reasonId: reason?.id,
        promoCodeId: activePromoCode?.id,
        note,
        // check level discounts do not care about itemIds, but it is required on the payload so just send empty array
        itemIds: [],
      },
    }, {
      onError: (e) => toast.show.serviceError(e, { id: `${sectionId}-sendStoreValues-createDiscount` }),
    });
  };

  const onDiscountAdd = (discount: StandardDiscountWithSchedules) => {
    setActiveDiscount(discount);
    const { type, reasons } = discount;
    if (type === 'OpenAmount' || type === 'OpenPercent') {
      openDiscountModal.open();
    } else if (reasons.length > 0 && activeReason === undefined) {
      reasonModal.open();
    } else {
      sendStoreValues();
    }
  };

  const onDiscountRemove = (discount: CheckDiscountUI) => {
    // use applied discount
    if (!discount.onCheck) return showToast({ message: t('discount.validation.checkDiscountMissing') });
    removeDiscount.mutate({ checkId: currentCheck.query.data?.id as number, discountId: discount.onCheck.id }, {
      onError: (e) => toast.show.serviceError(e, { id: `${sectionId}-onDiscountRemove-removeDiscount` }),
    });
  };

  const onUpdateAutoDiscount = (discount: AutoDiscountOptions) => {
    updateAutoDiscount.mutate({
      checkId: currentCheck.query.data?.id as number,
      discount,
    }, {
      onError: (e) => toast.show.serviceError(e, { id: `${sectionId}-onUpdateAutoDiscount-updateAutoDiscount` }),
    });
  };

  return {
    showToast,
    unpaidAmount,
    checkAmount,
    isApplicable,
    findDiscountOnCheck,
    discountUIs,
    autoDiscountUIs,
    getPromoCodeFromDiscount,
    getDiscountFromPromoCode,
    has,
    active: {
      reason: activeReason,
      promoCode: activePromoCode,
      openValue: activeOpenValue,
      discount: activeDiscount,
    },
    setActiveDiscount,
    setActiveOpenValue,
    setActivePromoCode,
    setActiveReason,
    modal: {
      reason: reasonModal,
      openDiscount: openDiscountModal,
    },
    onReasonSelect,
    onOpenDiscountApply,
    sendStoreValues,
    onDiscountAdd,
    onDiscountRemove,
    onUpdateAutoDiscount,
    updateAutoDiscount,
  };
};

export { useCheckDiscount };
export type { CheckDiscountUI };
